import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Plus, XCircle, Check, Trash2, Download, ChevronDown, ChevronRight, Pencil, X } from 'lucide-react'
import { QRCode } from 'react-qr-code'
import { downloadQRPng } from '../lib/printQR'
import { canCreateContent } from '../lib/eventLifecycle'
import { isContentEditable } from '../lib/eventPhase'

// Phase 2 of the FRAGO Rework:
// The legacy "INTEL" tab has been split. Intel triggers + scheduled
// deployments are absorbed into the FRAGO builder's activation triggers
// (Phase 3 / 4). What's left here is the catalogue of physical game
// objects — equipment and checkpoints — that FRAGO objectives can
// target. Same Cipher palette so the tab still feels like it belongs.

const C = {
  display: { fontFamily: "'VT323', 'Share Tech Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  panel: { background: 'rgba(0, 20, 8, .4)', border: '1px solid rgba(0,255,102,.18)', boxShadow: '0 0 6px rgba(0,255,102,.08)', position: 'relative' },
  input: { width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '8px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", outline: 'none' },
  select: { background: '#0a140a', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 },
  btnPrimary: { fontFamily: "'VT323', monospace", fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#00ff66', color: '#000', border: 'none', padding: '6px 14px', cursor: 'pointer', boxShadow: '0 0 6px #00ff66', display: 'inline-flex', alignItems: 'center', gap: 6 },
  btnSecondary: { fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'rgba(0, 20, 8, .4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', padding: '6px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
  tag: { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px' },
}

export default function EquipmentLocations({ event, user, isGm, bounties }) {
  const canCreate = isGm && canCreateContent(event?.status)
  // UX-1 — pre-live events allow inline edits on equipment / checkpoint
  // metadata. After kickoff the field is in motion and stealth edits
  // would confuse players.
  const canEditContent = isGm && isContentEditable(event?.status)

  // Per-card expansion + edit-mode state, keyed by row id.
  const [expandedEq, setExpandedEq] = useState({})
  const [editingEq, setEditingEq] = useState(null) // equipment id being edited
  const [eqDraft, setEqDraft] = useState({ name: '', description: '', location_hint: '' })
  const [expandedCp, setExpandedCp] = useState({})
  const [editingCp, setEditingCp] = useState(null)
  const [cpDraft, setCpDraft] = useState({ name: '', location_hint: '', capture_duration_seconds: '', points_on_capture: '', points_per_hold_interval: '', hold_interval_seconds: '' })

  const startEditEq = (eq) => {
    setEditingEq(eq.id)
    setEqDraft({ name: eq.name || '', description: eq.description || '', location_hint: eq.location_hint || '' })
    setExpandedEq(e => ({ ...e, [eq.id]: true }))
  }
  const saveEditEq = async (id) => {
    const { error } = await supabase.from('event_equipment').update({
      name: eqDraft.name.trim(), description: eqDraft.description.trim() || null, location_hint: eqDraft.location_hint.trim() || null,
    }).eq('id', id)
    if (error) { setMessage('Save failed: ' + error.message); return }
    setEditingEq(null); fetchAll()
  }

  const startEditCp = (cp) => {
    setEditingCp(cp.id)
    setCpDraft({
      name: cp.name || '',
      location_hint: cp.location_hint || '',
      capture_duration_seconds: cp.capture_duration_seconds ?? '',
      points_on_capture: cp.points_on_capture ?? '',
      points_per_hold_interval: cp.points_per_hold_interval ?? '',
      hold_interval_seconds: cp.hold_interval_seconds ?? '',
    })
    setExpandedCp(e => ({ ...e, [cp.id]: true }))
  }
  const saveEditCp = async (id) => {
    const numOrNull = (v) => v === '' || v == null ? null : parseInt(v)
    const { error } = await supabase.from('event_checkpoints').update({
      name: cpDraft.name.trim(),
      location_hint: cpDraft.location_hint.trim() || null,
      capture_duration_seconds: numOrNull(cpDraft.capture_duration_seconds),
      points_on_capture: numOrNull(cpDraft.points_on_capture) ?? 0,
      points_per_hold_interval: numOrNull(cpDraft.points_per_hold_interval) ?? 0,
      hold_interval_seconds: numOrNull(cpDraft.hold_interval_seconds) ?? 300,
    }).eq('id', id)
    if (error) { setMessage('Save failed: ' + error.message); return }
    setEditingCp(null); fetchAll()
  }

  const [equipment, setEquipment] = useState([])
  const [checkpoints, setCheckpoints] = useState([])
  const [message, setMessage] = useState('')

  // Equipment form
  const [showCreateEquip, setShowCreateEquip] = useState(false)
  const [equipQrPreviewId, setEquipQrPreviewId] = useState(null)
  const [eName, setEName] = useState('')
  const [eDescription, setEDescription] = useState('')
  const [eLocation, setELocation] = useState('')
  const [eSubmitting, setESubmitting] = useState(false)

  // Checkpoint form
  const [showCreateCp, setShowCreateCp] = useState(false)
  const [cpQrPreviewId, setCpQrPreviewId] = useState(null)
  const [cpName, setCpName] = useState('')
  const [cpLocation, setCpLocation] = useState('')
  const [cpDuration, setCpDuration] = useState('')
  const [cpPointsCapture, setCpPointsCapture] = useState('')
  const [cpPointsHold, setCpPointsHold] = useState('')
  const [cpHoldInterval, setCpHoldInterval] = useState('')
  const [cpLinkedBountyId, setCpLinkedBountyId] = useState('')
  const [cpSubmitting, setCpSubmitting] = useState(false)

  // Collapse state — persisted so the GM's choice sticks across visits.
  const [equipCollapsed, setEquipCollapsed] = useState(() => localStorage.getItem('dc_intel_equip_collapsed') === 'true')
  const [cpCollapsed, setCpCollapsed] = useState(() => localStorage.getItem('dc_intel_cp_collapsed') === 'true')
  const toggleCollapsed = (key) => {
    if (key === 'equip') {
      const next = !equipCollapsed
      setEquipCollapsed(next)
      localStorage.setItem('dc_intel_equip_collapsed', String(next))
    } else if (key === 'cp') {
      const next = !cpCollapsed
      setCpCollapsed(next)
      localStorage.setItem('dc_intel_cp_collapsed', String(next))
    }
  }

  useEffect(() => { fetchAll() }, [event.id])

  const fetchAll = async () => {
    const [eqRes, cpRes] = await Promise.all([
      supabase.from('event_equipment').select('*').eq('event_id', event.id).order('created_at', { ascending: false }),
      supabase.from('event_checkpoints')
        .select('*, controlling_faction:event_factions!event_checkpoints_controlling_faction_id_fkey(id, name, color), capturing_faction:event_factions!event_checkpoints_capturing_faction_id_fkey(id, name, color)')
        .eq('event_id', event.id).order('created_at', { ascending: false }),
    ])
    setEquipment(eqRes.data || [])
    setCheckpoints(cpRes.data || [])
  }

  // Checkpoint creation can still link to a queued FRAGO so a capture
  // activates the linked mission. After Phase 4 the FRAGO activation
  // path is the authoritative one, but this stays useful until the
  // legacy capture-side FRAGO hook is rewritten.
  const queuedBounties = bounties.filter(b => b.status === 'queued' || b.status === 'awaiting_delegation')

  // ── EQUIPMENT ───────────────────────────────────────────────────
  const createEquipment = async () => {
    if (!eName.trim()) { setMessage('Equipment name required.'); return }
    setESubmitting(true)
    const tempQr = 'ANUBIS-EQUIP-PENDING-' + Math.random().toString(36).slice(2, 10)
    const { data, error } = await supabase.from('event_equipment').insert({
      event_id: event.id,
      name: eName.trim(),
      equipment_qr: tempQr,
      description: eDescription.trim() || null,
      location_hint: eLocation.trim() || null,
      created_by: user.id,
    }).select('id').limit(1)
    if (error || !data?.[0]) {
      setMessage('Failed to create equipment: ' + (error?.message || 'unknown'))
      setESubmitting(false); return
    }
    const finalQr = 'ANUBIS-EQUIP-' + data[0].id
    await supabase.from('event_equipment').update({ equipment_qr: finalQr }).eq('id', data[0].id)
    setMessage('Equipment created — QR ready to print.')
    setShowCreateEquip(false); setEName(''); setEDescription(''); setELocation('')
    setESubmitting(false)
    fetchAll()
  }
  const setEquipmentStatus = async (id, newStatus) => {
    await supabase.from('event_equipment').update({ status: newStatus }).eq('id', id)
    fetchAll()
  }
  const deleteEquipment = async (id) => {
    if (!window.confirm('Delete this equipment? Linked FRAGO objectives will be unlinked.')) return
    await supabase.from('event_equipment').delete().eq('id', id)
    fetchAll()
  }

  // ── CHECKPOINTS ─────────────────────────────────────────────────
  const createCheckpoint = async () => {
    if (!cpName.trim()) { setMessage('Checkpoint name required.'); return }
    setCpSubmitting(true)
    const tempQr = 'ANUBIS-CP-PENDING-' + Math.random().toString(36).slice(2, 10)
    const { data, error } = await supabase.from('event_checkpoints').insert({
      event_id: event.id,
      name: cpName.trim(),
      checkpoint_qr: tempQr,
      location_hint: cpLocation.trim() || null,
      capture_duration_seconds: cpDuration ? parseInt(cpDuration) : null,
      points_on_capture: cpPointsCapture ? parseInt(cpPointsCapture) : 0,
      points_per_hold_interval: cpPointsHold ? parseInt(cpPointsHold) : 0,
      hold_interval_seconds: cpHoldInterval ? parseInt(cpHoldInterval) : 300,
      linked_bounty_id: cpLinkedBountyId || null,
      created_by: user.id,
    }).select('id').limit(1)
    if (error || !data?.[0]) {
      setMessage('Failed to create checkpoint: ' + (error?.message || 'unknown'))
      setCpSubmitting(false); return
    }
    const finalQr = 'ANUBIS-CP-' + data[0].id
    await supabase.from('event_checkpoints').update({ checkpoint_qr: finalQr }).eq('id', data[0].id)
    setMessage('Checkpoint created — QR ready to print.')
    setShowCreateCp(false)
    setCpName(''); setCpLocation(''); setCpDuration(''); setCpPointsCapture('')
    setCpPointsHold(''); setCpHoldInterval(''); setCpLinkedBountyId('')
    setCpSubmitting(false)
    fetchAll()
  }
  const deleteCheckpoint = async (id) => {
    if (!window.confirm('Delete this checkpoint? Scan history will cascade out.')) return
    await supabase.from('event_checkpoints').delete().eq('id', id)
    fetchAll()
  }
  const lockCheckpoint = async (id) => {
    await supabase.from('event_checkpoints').update({ status: 'locked' }).eq('id', id)
    fetchAll()
  }
  const unlockCheckpoint = async (id) => {
    await supabase.from('event_checkpoints').update({
      status: 'neutral',
      controlling_faction_id: null, capturing_faction_id: null,
      capture_started_at: null, capture_timer_remaining_seconds: null,
    }).eq('id', id)
    fetchAll()
  }

  return (
    <div>
      {message && (
        <div className="mb-3 p-2 text-xs" style={{ ...C.panel, color: message.includes('Failed') ? '#ff2a2a' : '#00ff66' }}>{message}</div>
      )}

      {/* ─────────── EQUIPMENT ─────────── */}
      <div className="flex justify-between items-center mb-3">
        {/* Track 6.2 — accordion toggle needs 44px min hit area so a
            thumb tap on mobile doesn't miss the chevron. */}
        <button onClick={() => toggleCollapsed('equip')} className="flex items-center gap-2"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9aff9a', padding: '8px 0', minHeight: 44 }}>
          <ChevronDown size={14} style={{ transform: equipCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }} />
          <h2 style={{ ...C.display, fontSize: 16, margin: 0 }}>
            ◇ EQUIPMENT ({equipment.length})
          </h2>
        </button>
        {canCreate && !equipCollapsed && (
          <button onClick={() => setShowCreateEquip(!showCreateEquip)} style={C.btnPrimary}>
            {showCreateEquip ? <><XCircle size={12} /> CANCEL</> : <><Plus size={12} /> NEW EQUIPMENT</>}
          </button>
        )}
      </div>
      {!equipCollapsed && (<>
        {showCreateEquip && (
          <div className="p-4 mb-4" style={{ ...C.panel, border: '1px solid #9aff9a' }}>
            <p style={{ ...C.display, fontSize: 14, color: '#9aff9a', marginBottom: 10 }}>◇ NEW EQUIPMENT</p>
            <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.mono} className="text-xs text-green-400 block mb-1">NAME</label>
                <input type="text" value={eName} onChange={e => setEName(e.target.value)} maxLength={64}
                  placeholder="Supply Crate Alpha" style={C.input} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.mono} className="text-xs text-green-400 block mb-1">DESCRIPTION (optional)</label>
                <input type="text" value={eDescription} onChange={e => setEDescription(e.target.value)} maxLength={200}
                  placeholder="Marked with red tape" style={C.input} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.mono} className="text-xs text-green-400 block mb-1">LOCATION HINT (GM-only reference)</label>
                <input type="text" value={eLocation} onChange={e => setELocation(e.target.value)} maxLength={120}
                  placeholder="Behind the south wall, under tarp" style={C.input} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={createEquipment} disabled={eSubmitting} style={{ ...C.btnPrimary, opacity: eSubmitting ? 0.5 : 1 }}>
                <Check size={14} /> CREATE + GENERATE QR
              </button>
              <button onClick={() => setShowCreateEquip(false)} style={C.btnSecondary}>CANCEL</button>
            </div>
          </div>
        )}

        {equipQrPreviewId && (() => {
          const eq = equipment.find(e => e.id === equipQrPreviewId)
          if (!eq) return null
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.85)' }} onClick={() => setEquipQrPreviewId(null)}>
              <div className="p-6" style={{ ...C.panel, background: '#0a140a', maxWidth: 360, width: '100%' }} onClick={e => e.stopPropagation()}>
                <p style={{ ...C.display, fontSize: 14, color: '#9aff9a', marginBottom: 12, textAlign: 'center' }}>◇ EQUIPMENT QR — {eq.name}</p>
                <div className="flex justify-center mb-3" style={{ background: '#fff', padding: 16 }}>
                  <QRCode id={`equip-qr-${eq.id}`} value={eq.equipment_qr} size={220} />
                </div>
                <p style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', textAlign: 'center', wordBreak: 'break-all' }}>{eq.equipment_qr}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => downloadQRPng({ svgElementId: `equip-qr-${eq.id}`, filename: `dc-equip-${(eq.name || 'item').replace(/\s+/g, '-')}` })} style={{ ...C.btnSecondary, flex: 1, justifyContent: 'center' }}>
                    <Download size={12} /> SAVE PNG
                  </button>
                  <button onClick={() => setEquipQrPreviewId(null)} style={{ ...C.btnPrimary, flex: 1, justifyContent: 'center' }}>CLOSE</button>
                </div>
              </div>
            </div>
          )
        })()}

        {equipment.length === 0 ? (
          <p className="text-center py-8" style={{ ...C.mono, fontSize: 12, color: '#2a6a3a' }}>NO EQUIPMENT</p>
        ) : (
          <div className="space-y-2">
            {equipment.map(eq => {
              const statusColor = eq.status === 'active' ? '#9aff9a' : eq.status === 'collected' ? '#ffcc33' : '#ff2a2a'
              const isExpanded = !!expandedEq[eq.id]
              const isEditing = editingEq === eq.id
              return (
                <div key={eq.id} style={{ ...C.panel, border: `1px solid ${statusColor}33` }}>
                  <div className="p-3 flex items-center justify-between gap-2">
                    <button onClick={() => setExpandedEq(e => ({ ...e, [eq.id]: !isExpanded }))}
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}>
                      {isExpanded ? <ChevronDown size={12} style={{ color: '#4aaa5a', flexShrink: 0 }} /> : <ChevronRight size={12} style={{ color: '#4aaa5a', flexShrink: 0 }} />}
                      <div className="min-w-0">
                        <p style={{ ...C.mono, fontSize: 12, color: '#e0e8de' }}>{eq.name}</p>
                        {!isExpanded && eq.description && <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0', marginTop: 2 }}>{eq.description}</p>}
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span style={{ ...C.tag, color: statusColor, border: `1px solid ${statusColor}66`, background: statusColor + '14' }}>
                        ● {eq.status.toUpperCase()}
                      </span>
                      <button onClick={() => setEquipQrPreviewId(eq.id)} style={{ ...C.btnSecondary, fontSize: 10 }}>QR</button>
                      {isGm && eq.status === 'active' && (
                        <select onChange={e => { if (e.target.value) setEquipmentStatus(eq.id, e.target.value); e.target.value = '' }} value=""
                          style={{ ...C.select, fontSize: 10 }}>
                          <option value="">…</option>
                          <option value="collected">Mark collected</option>
                          <option value="destroyed">Mark destroyed</option>
                        </select>
                      )}
                      {isGm && <button onClick={() => deleteEquipment(eq.id)} style={{ background: 'none', border: 'none', color: '#2a6a3a', cursor: 'pointer' }}><Trash2 size={12} /></button>}
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '0 12px 12px 28px', borderTop: '1px solid rgba(0,255,102,.1)' }}>
                      {isEditing ? (
                        <div className="grid gap-2 pt-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', display: 'block', marginBottom: 2 }}>NAME</label>
                            <input value={eqDraft.name} onChange={e => setEqDraft(d => ({ ...d, name: e.target.value }))} maxLength={80} style={C.input} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', display: 'block', marginBottom: 2 }}>DESCRIPTION</label>
                            <input value={eqDraft.description} onChange={e => setEqDraft(d => ({ ...d, description: e.target.value }))} maxLength={200} style={C.input} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', display: 'block', marginBottom: 2 }}>LOCATION HINT (GM-only)</label>
                            <input value={eqDraft.location_hint} onChange={e => setEqDraft(d => ({ ...d, location_hint: e.target.value }))} maxLength={120} style={C.input} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }} className="flex gap-2">
                            <button onClick={() => saveEditEq(eq.id)} style={C.btnPrimary}><Check size={12} /> SAVE</button>
                            <button onClick={() => setEditingEq(null)} style={C.btnSecondary}><X size={12} /> CANCEL</button>
                          </div>
                        </div>
                      ) : (
                        <div className="pt-3 space-y-1">
                          {eq.description && <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0' }}><span style={{ color: '#4aaa5a' }}>DESC:</span> {eq.description}</p>}
                          {eq.location_hint && <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0' }}><span style={{ color: '#4aaa5a' }}>LOC:</span> {eq.location_hint}</p>}
                          <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0' }}><span style={{ color: '#4aaa5a' }}>QR:</span> <span style={{ wordBreak: 'break-all' }}>{eq.equipment_qr}</span></p>
                          {canEditContent && (
                            <button onClick={() => startEditEq(eq)} style={{ ...C.btnSecondary, marginTop: 6 }}><Pencil size={11} /> EDIT</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </>)}

      {/* ─────────── CHECKPOINTS ─────────── */}
      <div className="flex justify-between items-center mb-3 mt-6">
        {/* Track 6.2 — accordion toggle 44px min. */}
        <button onClick={() => toggleCollapsed('cp')} className="flex items-center gap-2"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4dd6ff', padding: '8px 0', minHeight: 44 }}>
          <ChevronDown size={14} style={{ transform: cpCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }} />
          <h2 style={{ ...C.display, fontSize: 16, color: '#4dd6ff', margin: 0 }}>
            ⌖ CHECKPOINTS ({checkpoints.length})
          </h2>
        </button>
        {canCreate && !cpCollapsed && (
          <button onClick={() => setShowCreateCp(!showCreateCp)} style={C.btnPrimary}>
            {showCreateCp ? <><XCircle size={12} /> CANCEL</> : <><Plus size={12} /> NEW CHECKPOINT</>}
          </button>
        )}
      </div>

      {!cpCollapsed && (<>
        {showCreateCp && (
          <div className="p-4 mb-4" style={{ ...C.panel, border: '1px solid #4dd6ff' }}>
            <p style={{ ...C.display, fontSize: 14, color: '#4dd6ff', marginBottom: 10 }}>⌖ NEW CHECKPOINT</p>
            <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.mono} className="text-xs text-cyan-400 block mb-1">NAME *</label>
                <input type="text" value={cpName} onChange={e => setCpName(e.target.value)} maxLength={64}
                  placeholder="ALPHA OBJECTIVE" style={C.input} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.mono} className="text-xs text-cyan-400 block mb-1">LOCATION HINT</label>
                <input type="text" value={cpLocation} onChange={e => setCpLocation(e.target.value)} maxLength={120}
                  placeholder="Hilltop bunker, north of grid 14" style={C.input} />
              </div>
              <div>
                <label style={C.mono} className="text-xs text-cyan-400 block mb-1">CAPTURE DURATION (sec, blank = org default)</label>
                <input type="number" value={cpDuration} onChange={e => setCpDuration(e.target.value)} min={5} max={3600} style={C.input} placeholder="300" />
              </div>
              <div>
                <label style={C.mono} className="text-xs text-cyan-400 block mb-1">POINTS ON CAPTURE (0 = org default)</label>
                <input type="number" value={cpPointsCapture} onChange={e => setCpPointsCapture(e.target.value)} min={0} max={5000} style={C.input} placeholder="0" />
              </div>
              <div>
                <label style={C.mono} className="text-xs text-cyan-400 block mb-1">POINTS PER HOLD INTERVAL</label>
                <input type="number" value={cpPointsHold} onChange={e => setCpPointsHold(e.target.value)} min={0} max={1000} style={C.input} placeholder="0" />
              </div>
              <div>
                <label style={C.mono} className="text-xs text-cyan-400 block mb-1">HOLD INTERVAL (sec)</label>
                <input type="number" value={cpHoldInterval} onChange={e => setCpHoldInterval(e.target.value)} min={30} max={3600} style={C.input} placeholder="300" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.mono} className="text-xs text-cyan-400 block mb-1">LINKED FRAGO (fires on capture, optional)</label>
                <select value={cpLinkedBountyId} onChange={e => setCpLinkedBountyId(e.target.value)} style={{ ...C.select, width: '100%' }}>
                  <option value="">— none —</option>
                  {queuedBounties.map(b => (
                    <option key={b.id} value={b.id} style={{ background: '#0a140a' }}>
                      {b.mission_name || b.bounty_type} ({b.points} pts) · {b.status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={createCheckpoint} disabled={cpSubmitting} style={{ ...C.btnPrimary, opacity: cpSubmitting ? 0.5 : 1 }}>
                <Check size={14} /> {cpSubmitting ? 'CREATING...' : 'CREATE + GENERATE QR'}
              </button>
              <button onClick={() => setShowCreateCp(false)} style={C.btnSecondary}>CANCEL</button>
            </div>
          </div>
        )}

        {cpQrPreviewId && (() => {
          const cp = checkpoints.find(c => c.id === cpQrPreviewId)
          if (!cp) return null
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.85)' }} onClick={() => setCpQrPreviewId(null)}>
              <div className="p-6" style={{ ...C.panel, background: '#0a140a', maxWidth: 360, width: '100%' }} onClick={e => e.stopPropagation()}>
                <p style={{ ...C.display, fontSize: 14, color: '#4dd6ff', marginBottom: 12, textAlign: 'center' }}>⌖ CHECKPOINT QR — {cp.name}</p>
                <div className="flex justify-center mb-3" style={{ background: '#fff', padding: 16 }}>
                  <QRCode id={`cp-qr-${cp.id}`} value={cp.checkpoint_qr} size={220} />
                </div>
                <p style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', textAlign: 'center', wordBreak: 'break-all' }}>{cp.checkpoint_qr}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => downloadQRPng({ svgElementId: `cp-qr-${cp.id}`, filename: `dc-cp-${(cp.name || 'cp').replace(/\s+/g, '-')}` })} style={{ ...C.btnSecondary, flex: 1, justifyContent: 'center' }}>
                    <Download size={12} /> SAVE PNG
                  </button>
                  <button onClick={() => setCpQrPreviewId(null)} style={{ ...C.btnPrimary, flex: 1, justifyContent: 'center' }}>CLOSE</button>
                </div>
              </div>
            </div>
          )
        })()}

        {checkpoints.length === 0 ? (
          <p className="text-center py-8" style={{ ...C.mono, fontSize: 12, color: '#2a6a3a' }}>NO CHECKPOINTS</p>
        ) : (
          <div className="space-y-2">
            {checkpoints.map(cp => {
              const factionTag = cp.controlling_faction || cp.capturing_faction
              const statusColor = cp.status === 'captured' ? '#9aff9a'
                : cp.status === 'capturing' ? '#ffcc33'
                : cp.status === 'contested' ? '#ff7a18'
                : cp.status === 'locked'    ? '#5a6470'
                : '#4dd6ff'
              const isExpanded = !!expandedCp[cp.id]
              const isEditing = editingCp === cp.id
              return (
                <div key={cp.id} style={{ ...C.panel, border: `1px solid ${statusColor}55` }}>
                  <div className="p-3 flex items-center justify-between gap-2">
                    <button onClick={() => setExpandedCp(e => ({ ...e, [cp.id]: !isExpanded }))}
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}>
                      {isExpanded ? <ChevronDown size={12} style={{ color: '#4dd6ff', flexShrink: 0 }} /> : <ChevronRight size={12} style={{ color: '#4dd6ff', flexShrink: 0 }} />}
                      <div className="min-w-0">
                        <p style={{ ...C.mono, fontSize: 12, color: '#e0e8de' }}>{cp.name}</p>
                        {!isExpanded && cp.location_hint && <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0', marginTop: 2 }}>📍 {cp.location_hint}</p>}
                        {factionTag && (
                          <p style={{ ...C.mono, fontSize: 10, color: factionTag.color, marginTop: 2 }}>
                            {cp.status === 'capturing' ? '⏳ ' : '◆ '}{factionTag.name}
                          </p>
                        )}
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span style={{ ...C.tag, color: statusColor, border: `1px solid ${statusColor}66`, background: statusColor + '14' }}>
                        ● {cp.status.toUpperCase()}
                      </span>
                      <button onClick={() => setCpQrPreviewId(cp.id)} style={{ ...C.btnSecondary, fontSize: 10 }}>QR</button>
                      {isGm && cp.status !== 'locked' && (
                        <button onClick={() => lockCheckpoint(cp.id)} style={{ ...C.btnSecondary, fontSize: 10 }}>LOCK</button>
                      )}
                      {isGm && cp.status === 'locked' && (
                        <button onClick={() => unlockCheckpoint(cp.id)} style={{ ...C.btnSecondary, fontSize: 10 }}>UNLOCK</button>
                      )}
                      {isGm && <button onClick={() => deleteCheckpoint(cp.id)} style={{ background: 'none', border: 'none', color: '#2a6a3a', cursor: 'pointer' }}><Trash2 size={12} /></button>}
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '0 12px 12px 28px', borderTop: '1px solid rgba(77,214,255,.15)' }}>
                      {isEditing ? (
                        <div className="grid gap-2 pt-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4dd6ff', display: 'block', marginBottom: 2 }}>NAME</label>
                            <input value={cpDraft.name} onChange={e => setCpDraft(d => ({ ...d, name: e.target.value }))} maxLength={64} style={C.input} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4dd6ff', display: 'block', marginBottom: 2 }}>LOCATION HINT</label>
                            <input value={cpDraft.location_hint} onChange={e => setCpDraft(d => ({ ...d, location_hint: e.target.value }))} maxLength={120} style={C.input} />
                          </div>
                          <div>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4dd6ff', display: 'block', marginBottom: 2 }}>CAPTURE (sec)</label>
                            <input type="number" value={cpDraft.capture_duration_seconds} onChange={e => setCpDraft(d => ({ ...d, capture_duration_seconds: e.target.value }))} min={5} max={3600} style={C.input} />
                          </div>
                          <div>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4dd6ff', display: 'block', marginBottom: 2 }}>PTS ON CAPTURE</label>
                            <input type="number" value={cpDraft.points_on_capture} onChange={e => setCpDraft(d => ({ ...d, points_on_capture: e.target.value }))} min={0} max={5000} style={C.input} />
                          </div>
                          <div>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4dd6ff', display: 'block', marginBottom: 2 }}>PTS / HOLD</label>
                            <input type="number" value={cpDraft.points_per_hold_interval} onChange={e => setCpDraft(d => ({ ...d, points_per_hold_interval: e.target.value }))} min={0} max={5000} style={C.input} />
                          </div>
                          <div>
                            <label style={{ ...C.mono, fontSize: 10, color: '#4dd6ff', display: 'block', marginBottom: 2 }}>HOLD INTERVAL (sec)</label>
                            <input type="number" value={cpDraft.hold_interval_seconds} onChange={e => setCpDraft(d => ({ ...d, hold_interval_seconds: e.target.value }))} min={30} max={3600} style={C.input} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }} className="flex gap-2">
                            <button onClick={() => saveEditCp(cp.id)} style={C.btnPrimary}><Check size={12} /> SAVE</button>
                            <button onClick={() => setEditingCp(null)} style={C.btnSecondary}><X size={12} /> CANCEL</button>
                          </div>
                        </div>
                      ) : (
                        <div className="pt-3 space-y-1">
                          {cp.location_hint && <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0' }}><span style={{ color: '#4dd6ff' }}>LOC:</span> {cp.location_hint}</p>}
                          <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0' }}><span style={{ color: '#4dd6ff' }}>CAPTURE:</span> {cp.capture_duration_seconds ?? '—'}s · <span style={{ color: '#4dd6ff' }}>PTS:</span> {cp.points_on_capture ?? 0} capture · {cp.points_per_hold_interval ?? 0} / {cp.hold_interval_seconds ?? 300}s hold</p>
                          <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0' }}><span style={{ color: '#4dd6ff' }}>QR:</span> <span style={{ wordBreak: 'break-all' }}>{cp.checkpoint_qr}</span></p>
                          {canEditContent && (
                            <button onClick={() => startEditCp(cp)} style={{ ...C.btnSecondary, marginTop: 6 }}><Pencil size={11} /> EDIT</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </>)}
    </div>
  )
}
